import { useState } from 'react'

import { CopyIcon } from '@chakra-ui/icons'
import {
  Box,
  HStack,
  IconButton,
  PopoverBody,
  PopoverContent,
  Text,
  useToast,
} from '@chakra-ui/react'

interface ClickToCopyPopoverIProps {
  displayText: string
  textToCopy?: string
}

const showToastDuration = 2_000

/**
 * A component that displays text in a popover with a copy-to-clipboard button.
 * Shows a success toast notification when text is copied and an error toast
 * if copying fails.
 *
 * @param displayText - The text to display in the popover
 * @param textToCopy - The text to copy to clipboard (defaults to `displayText` if not provided)
 */
export const ClickToCopyPopover = ({
  displayText,
  textToCopy = displayText,
}: ClickToCopyPopoverIProps) => {
  const toast = useToast()
  const [isCopied, setIsCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy)
      setIsCopied(true)
      toast({
        title: 'Copied to clipboard',
        status: 'success',
        duration: showToastDuration,
        isClosable: true,
      })

      setTimeout(() => {
        setIsCopied(false)
      }, showToastDuration)
    } catch (err) {
      toast({
        title: 'Failed to copy',
        status: 'error',
        duration: showToastDuration,
        isClosable: true,
      })
    }
  }

  return (
    <PopoverContent p={0} width='auto'>
      <PopoverBody>
        <HStack justifyContent='space-between' maxWidth={500} spacing={2}>
          <Box minWidth='0'>
            <Text noOfLines={4}>{displayText}</Text>
          </Box>
          <IconButton
            aria-label='Copy to clipboard'
            colorScheme={isCopied ? 'green' : 'gray'}
            flexShrink={0}
            icon={<CopyIcon />}
            onClick={handleCopy}
            size='sm'
          />
        </HStack>
      </PopoverBody>
    </PopoverContent>
  )
}
